// Live Chart of Accounts / General Ledger / Trial Balance / Properties API
// (/api/v1/business/gl-accounts, /journal-entries, /reports, /properties).
// Replaces the mock glService when the `business_accounting` flag is on.
//
// All calls are business-scoped: the X-Business-Id header is injected by the
// businessApi* helpers from the active business set by BusinessContext.
// Money is decimal STRINGS on the wire — see types/businessApi.ts.

import {
  businessApiGet,
  businessApiPost,
  businessApiPatch,
  businessApiDelete,
} from '../utils/apiUtils';
import { parseData, parseEnvelope, buildQuery } from './businessApiCommon';
import { segmentFilterQueryParams } from '../components/business/segments/segmentReportFilter';
import { SegmentFilterQuery } from '../types/segmentsApi';
import { registerFundQueryParams } from '../components/business/funds/fundReportFilter';
import { FundFilterQuery, TrialBalanceByFundReport } from '../types/fundsApi';
import {
  CreateGlAccountParams,
  CreateJournalEntryParams,
  CursorPage,
  GlAccountFilters,
  GlRegisterGroupedPage,
  GlRegisterGroupRow,
  GlRegisterPage,
  GlRegisterRow,
  GlRegisterSummary,
  JournalEntriesQuery,
  LiveGlAccount,
  LiveJournalEntry,
  LiveProperty,
  PropertyActivityQuery,
  PropertyActivityRow,
  PropertyFilters,
  PropertyParams,
  RegisterQuery,
  ReverseJournalEntryParams,
  TrialBalanceReport,
  UpdateGlAccountParams,
  UUID,
} from '../types/businessApi';

const BASE = '/api/v1/business';

class BusinessGlService {
  // ---- Chart of Accounts ----

  /** GET /gl-accounts — full COA with computed balances. */
  async listAccounts(filters: GlAccountFilters = {}): Promise<LiveGlAccount[]> {
    const query = buildQuery({
      category: filters.category,
      active: filters.active,
      search: filters.search,
    });
    const response = await businessApiGet(`${BASE}/gl-accounts${query}`);
    return parseData<LiveGlAccount[]>(response);
  }

  /** GET /gl-accounts/:id */
  async getAccount(accountId: UUID): Promise<LiveGlAccount> {
    const response = await businessApiGet(`${BASE}/gl-accounts/${accountId}`);
    return parseData<LiveGlAccount>(response);
  }

  /** POST /gl-accounts (owner/accountant only) */
  async createAccount(params: CreateGlAccountParams): Promise<LiveGlAccount> {
    const response = await businessApiPost(`${BASE}/gl-accounts`, params);
    return parseData<LiveGlAccount>(response);
  }

  /** PATCH /gl-accounts/:id — name/description/active/account_number only. */
  async updateAccount(accountId: UUID, params: UpdateGlAccountParams): Promise<LiveGlAccount> {
    const response = await businessApiPatch(`${BASE}/gl-accounts/${accountId}`, params);
    return parseData<LiveGlAccount>(response);
  }

  /** DELETE /gl-accounts/:id — only unprotected accounts with zero history. */
  async deleteAccount(accountId: UUID): Promise<void> {
    const response = await businessApiDelete(`${BASE}/gl-accounts/${accountId}`);
    if (!response.ok) await parseData(response); // throws BusinessApiError
  }

  /**
   * GET /gl-accounts/:id/register — checkbook-register rows with a running
   * balance, cursor-paginated in chronological order. meta carries the
   * WINDOW's opening/closing balances and debit/credit totals (whole filter
   * window, not per page) — surfaced as `summary`.
   */
  getAccountRegister(
    accountId: UUID,
    query?: Omit<RegisterQuery, 'group'> & SegmentFilterQuery
  ): Promise<GlRegisterPage>;
  /** With `group`, some rows may be folded ones (`GlRegisterGroupRow`): no id, no entry. */
  getAccountRegister(accountId: UUID, query: RegisterQuery & SegmentFilterQuery): Promise<GlRegisterGroupedPage>;
  /**
   * PRD 137 §4.5: with a segment filter the rows are that segment's lines and
   * the opening, running and closing balances are the segment's own.
   */
  async getAccountRegister(
    accountId: UUID,
    query: RegisterQuery & SegmentFilterQuery = {}
  ): Promise<GlRegisterGroupedPage> {
    const qs = buildQuery({
      from: query.from,
      to: query.to,
      source_type: query.source_type,
      cursor: query.cursor,
      limit: query.limit,
      group: query.group,
      ...segmentFilterQueryParams(query.segment),
    });
    const response = await businessApiGet(`${BASE}/gl-accounts/${accountId}/register${qs}`);
    const { data, meta } = await parseEnvelope<Array<GlRegisterRow | GlRegisterGroupRow>>(response);
    const summary = meta as Partial<GlRegisterSummary>;
    return {
      rows: data,
      nextCursor: (meta.next_cursor as string | null) ?? null,
      summary: {
        opening_balance: summary.opening_balance ?? '0.0000',
        closing_balance: summary.closing_balance ?? '0.0000',
        total_debits: summary.total_debits ?? '0.0000',
        total_credits: summary.total_credits ?? '0.0000',
      },
    };
  }

  // ---- Journal entries ----

  /** GET /journal-entries — newest first, cursor-paginated (no lines). */
  async listJournalEntries(
    query: JournalEntriesQuery & SegmentFilterQuery & FundFilterQuery = {}
  ): Promise<CursorPage<LiveJournalEntry>> {
    const qs = buildQuery({
      from: query.from,
      to: query.to,
      source_type: query.source_type,
      source_category: query.source_category,
      customer_id: query.customer_id,
      vendor_id: query.vendor_id,
      property_id: query.property_id,
      cursor: query.cursor,
      limit: query.limit,
      // PRD 137 §4.5: entries with a line in the segment. Nothing without one.
      ...segmentFilterQueryParams(query.segment),
      // PRD 83 Wave 2: entries with a line in the fund, or with an unassigned
      // line. Nothing without one.
      ...registerFundQueryParams(query.fund),
    });
    const response = await businessApiGet(`${BASE}/journal-entries${qs}`);
    const { data, meta } = await parseEnvelope<LiveJournalEntry[]>(response);
    return { rows: data, nextCursor: (meta.next_cursor as string | null) ?? null };
  }

  /** GET /journal-entries/:id — detail with lines and totals. */
  async getJournalEntry(entryId: UUID): Promise<LiveJournalEntry> {
    const response = await businessApiGet(`${BASE}/journal-entries/${entryId}`);
    return parseData<LiveJournalEntry>(response);
  }

  /**
   * POST /journal-entries — post a manual, balanced entry (owner/accountant).
   * Line amounts MUST be decimal strings. Supports the Idempotency-Key header
   * so a retried submit can't double-post.
   */
  async createJournalEntry(
    params: CreateJournalEntryParams,
    idempotencyKey?: string
  ): Promise<LiveJournalEntry> {
    const headers: Record<string, string> = {};
    if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
    const response = await businessApiPost(`${BASE}/journal-entries`, params, { headers });
    return parseData<LiveJournalEntry>(response);
  }

  /** POST /journal-entries/:id/reverse — posted entries are immutable. */
  async reverseJournalEntry(
    entryId: UUID,
    params: ReverseJournalEntryParams = {}
  ): Promise<LiveJournalEntry> {
    const response = await businessApiPost(`${BASE}/journal-entries/${entryId}/reverse`, params);
    return parseData<LiveJournalEntry>(response);
  }

  // ---- Reports ----

  /** GET /reports/trial-balance?as_of=YYYY-MM-DD (defaults to today). */
  async getTrialBalance(asOf?: string): Promise<TrialBalanceReport> {
    const qs = buildQuery({ as_of: asOf });
    const response = await businessApiGet(`${BASE}/reports/trial-balance${qs}`);
    return parseData<TrialBalanceReport>(response);
  }

  /**
   * GET /reports/trial-balance?group_by=fund (PRD 83 Wave 2): a column per
   * fund, plus Unassigned for lines posted before funds were turned on. Ask
   * only while funds are on.
   */
  async getTrialBalanceByFund(asOf?: string): Promise<TrialBalanceByFundReport> {
    const qs = buildQuery({ as_of: asOf, group_by: 'fund' });
    const response = await businessApiGet(`${BASE}/reports/trial-balance${qs}`);
    return parseData<TrialBalanceByFundReport>(response);
  }

  // ---- Properties (dimension tagging, PRD 04) ----

  /** GET /properties — active by default; pass filters to widen. */
  async listProperties(filters: PropertyFilters = {}): Promise<LiveProperty[]> {
    const qs = buildQuery({
      kind: filters.kind,
      status: filters.status,
      include_archived: filters.include_archived,
    });
    const response = await businessApiGet(`${BASE}/properties${qs}`);
    return parseData<LiveProperty[]>(response);
  }

  /** GET /properties/:id */
  async getProperty(propertyId: UUID): Promise<LiveProperty> {
    const response = await businessApiGet(`${BASE}/properties/${propertyId}`);
    return parseData<LiveProperty>(response);
  }

  /** POST /properties */
  async createProperty(params: PropertyParams): Promise<LiveProperty> {
    const response = await businessApiPost(`${BASE}/properties`, params);
    return parseData<LiveProperty>(response);
  }

  /** PATCH /properties/:id */
  async updateProperty(propertyId: UUID, params: PropertyParams): Promise<LiveProperty> {
    const response = await businessApiPatch(`${BASE}/properties/${propertyId}`, params);
    return parseData<LiveProperty>(response);
  }

  /** POST /properties/:id/archive — archive-only lifecycle, no delete. */
  async archiveProperty(propertyId: UUID): Promise<LiveProperty> {
    const response = await businessApiPost(`${BASE}/properties/${propertyId}/archive`);
    return parseData<LiveProperty>(response);
  }

  /** POST /properties/:id/unarchive */
  async unarchiveProperty(propertyId: UUID): Promise<LiveProperty> {
    const response = await businessApiPost(`${BASE}/properties/${propertyId}/unarchive`);
    return parseData<LiveProperty>(response);
  }

  /** GET /properties/:id/activity — journal lines tagged to the property. */
  async getPropertyActivity(
    propertyId: UUID,
    query: PropertyActivityQuery = {}
  ): Promise<CursorPage<PropertyActivityRow>> {
    const qs = buildQuery({
      from: query.from,
      to: query.to,
      cursor: query.cursor,
      limit: query.limit,
    });
    const response = await businessApiGet(`${BASE}/properties/${propertyId}/activity${qs}`);
    const { data, meta } = await parseEnvelope<PropertyActivityRow[]>(response);
    return { rows: data, nextCursor: (meta.next_cursor as string | null) ?? null };
  }
}

export const businessGlService = new BusinessGlService();
